import type { GridApi, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowTreeSelector } from '@mui/x-data-grid-pro';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/common/api/items';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/common/api/types/items';
import {
  captureTreeSetBaseline,
  FAST_POLL_MAX_ATTEMPTS,
  invalidateItemsTreeSet,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { isPriorityOrderedTreeSetKey } from '@/features/items/queryKeys/itemsTreeSetKey';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';

// Exclusive end index of rootId's subtree in the flat DFS array.
const subtreeEndIndex = (
  entries: readonly ItemTreeSetEntry[],
  byId: ReadonlyMap<number, ItemTreeSetEntry>,
  startIdx: number,
  rootId: number,
): number => {
  let end = startIdx + 1;
  while (end < entries.length) {
    let cur: ItemTreeSetEntry | undefined = entries[end];
    let isDescendant = false;
    while (cur?.parentId != null) {
      if (cur.parentId === rootId) {
        isDescendant = true;
        break;
      }
      cur = byId.get(cur.parentId);
    }
    if (!isDescendant) break;
    end++;
  }
  return end;
};

// Returns the original array unchanged if the move would cross parents or an id is missing.
export const reorderTreeSetData = (
  entries: readonly ItemTreeSetEntry[],
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
): readonly ItemTreeSetEntry[] => {
  const movedIdx = entries.findIndex((e) => e.id === movedId);
  if (movedIdx === -1) return entries;

  const byId = new Map(entries.map((e) => [e.id, e]));
  const movedParentId = entries[movedIdx]?.parentId ?? null;
  const subtreeEnd = subtreeEndIndex(entries, byId, movedIdx, movedId);
  const subtree = entries.slice(movedIdx, subtreeEnd);
  const withoutSubtree = [...entries.slice(0, movedIdx), ...entries.slice(subtreeEnd)];

  const targetIdx = withoutSubtree.findIndex((e) => e.id === targetSiblingId);
  if (targetIdx === -1) return entries;
  if ((withoutSubtree[targetIdx]?.parentId ?? null) !== movedParentId) return entries;

  const insertAt =
    position === 'above'
      ? targetIdx
      : subtreeEndIndex(withoutSubtree, byId, targetIdx, targetSiblingId);

  return [...withoutSubtree.slice(0, insertAt), ...subtree, ...withoutSubtree.slice(insertAt)];
};

const reorderInCache = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  // Priority-ordered variants only — a sorted view keeps the server's order untouched.
  queryClient.setQueriesData<ItemTreeSet>(
    {
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: isPriorityOrderedTreeSetKey,
    },
    (cached) => {
      if (!cached) return cached;
      const data = reorderTreeSetData(cached.data, movedId, targetSiblingId, position);
      if (data === cached.data) return cached;
      return { ...cached, data: [...data] };
    },
  );
};

export const useItemPriorityReorder = (apiRef: React.RefObject<GridApi>, instanceKey: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return React.useCallback(
    async (params: GridRowOrderChangeParams) => {
      const tree = gridRowTreeSelector(apiRef);
      const parentId = params.newParent ?? GRID_ROOT_GROUP_ID;
      const parentNode = tree[parentId];
      if (!parentNode || !('children' in parentNode)) return;

      const { children } = parentNode;
      const idx = params.targetIndex;
      const previousItemId = idx > 0 ? Number(children[idx - 1]) : undefined;
      const nextItemId = idx < children.length - 1 ? Number(children[idx + 1]) : undefined;

      let relativePriority: { id: number; type: 'before' | 'after' } | undefined;
      if (previousItemId !== undefined) {
        relativePriority = { id: previousItemId, type: 'after' };
      } else if (nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else {
        return;
      }

      // Captured before the optimistic patch so the poll's baseline is the pre-move order. The
      // poll itself starts after the mutation succeeds, not here, so it isn't burning its attempt
      // budget on the mutation's own round trip.
      const treeSetBaseline = captureTreeSetBaseline(queryClient);

      // Deferred past the drop frame so the cache rebuild doesn't race setRowPosition's repaint.
      const anchorId = relativePriority.id;
      const cachePosition = relativePriority.type === 'after' ? 'below' : 'above';
      const reconcileHandle = requestAnimationFrame(() => {
        reorderInCache(queryClient, params.row.id as number, anchorId, cachePosition);
      });

      try {
        await ItemsApi.updateItem({
          itemId: params.row.id as number,
          data: { relativePriority },
        });
      } catch {
        // Cancel the pending reconcile so the rollback below is the last write to the cache.
        cancelAnimationFrame(reconcileHandle);
        toast.error(
          t(($) => $.common.errors.global.title),
          {
            description: t(($) => $.common.errors.genericWithSupport),
          },
        );

        const api = apiRef.current;
        if (!api) return;

        const oldParentId = params.oldParent ?? GRID_ROOT_GROUP_ID;
        const oldParentNode = gridRowTreeSelector(apiRef)[oldParentId];
        if (!oldParentNode || !('children' in oldParentNode)) return;

        const sourceId = params.row.id as string | number;
        const siblings = oldParentNode.children.filter((id) => String(id) !== String(sourceId));
        const anchor = params.oldIndex === 0 ? siblings[0] : siblings[params.oldIndex - 1];
        const position = params.oldIndex === 0 ? 'above' : 'below';

        if (anchor === undefined) return;

        api.setRowPosition(sourceId, anchor, position);
        reorderInCache(queryClient, params.row.id as number, Number(anchor), position);
        bumpVisibleItemsCache(queryClient, instanceKey);
        return;
      }

      void invalidateItemsTreeSet(queryClient);
      // Not deferred like the rAF write above — there's no drop-frame repaint left to race by the
      // time a poll attempt calls this. Running it synchronously, in the same tick as the
      // attempt's own cache write, lets React batch both so the stale response never paints.
      void reconcileAndSettle(queryClient, [params.row.id as number], {
        baseline: treeSetBaseline,
        // Overrides reconcileAndSettle's shorter diff-mode default — see DIFF_MODE_MAX_ATTEMPTS.
        maxAttempts: FAST_POLL_MAX_ATTEMPTS,
        reassertLocalWrite: () =>
          reorderInCache(queryClient, params.row.id as number, anchorId, cachePosition),
      });
    },
    [apiRef, queryClient, t, instanceKey],
  );
};
